import { Component, inject, input, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { RestrictionsProfile } from '@rocksoft/mdm-sdk-v2';
import { Observable, Subject, merge } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AppleCommandProgressService } from '../../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../../shared/services/apple-mdm-service';
import { ConfigurationPayloadComponent } from '../../../../sub-features/apple-profiles/profile-components/configuration-payload/configuration-payload.component';
import { ConfigurationProfileComponent } from '../../../../sub-features/apple-profiles/profile-components/configuration-profile/configuration-profile.component';
import { RestrictionsSettingsComponent } from '../../../../sub-features/apple-profiles/profile-components/restrictions-profile/restrictions-settings/restrictions-settings.component';
import { CommandSpinnerComponent } from '../../../../utilities/command-spinner/command-spinner.component';
import { ConfigurationProfile } from '../../../../viewmodels/apple/configuration-profile.model';
import { PayloadContent } from '../../../../viewmodels/apple/profiles/payload-content.model';
import { CommandProgress } from '../../../../viewmodels/apple/signalr/command-context.model';

@Component({
  selector: 'ml-mdm-apple-create-restrictions-profile',
  templateUrl: './apple-create-restrictions-profile.component.html',
  imports: [
    CommandSpinnerComponent,
    RestrictionsSettingsComponent,
    ConfigurationPayloadComponent,
    ConfigurationProfileComponent,
    FaIconComponent,
  ],
})
export class AppleCreateRestrictionsProfileComponent {
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly mdmService = inject(AppleMdmService);

  private readonly restrictionsProfileComponent = viewChild.required(RestrictionsSettingsComponent);

  private readonly configurationProfileComponent = viewChild.required(ConfigurationProfileComponent);

  public payloadContent: RestrictionsProfile = {
    allowAccountModification: true,
    allowActivityContinuation: true,
    allowAddingGameCenterFriends: true,
    allowAirDrop: true,
    allowAirPlayIncomingRequests: true,
    allowAirPrint: true,
    allowAirPrintCredentialsStorage: true,
    allowAirPrintiBeaconDiscovery: true,
    allowAppCellularDataModification: true,
    allowAppClips: true,
    allowAppInstallation: true,
    allowApplePersonalizedAdvertising: true,
    allowAppRemoval: true,
    allowAssistant: true,
    allowAssistantUserGeneratedContent: true,
    allowAssistantWhileLocked: true,
    allowAutoCorrection: true,
    allowAutomaticAppDownloads: true,
    allowAutoUnlock: true,
    allowBluetoothModification: true,
    allowBookstore: true,
    allowBookstoreErotica: true,
    allowCamera: true,
    allowCellularPlanModification: true,
    allowChat: true,
    allowCloudAddressBook: true,
    allowCloudBackup: true,
    allowCloudBookmarks: true,
    allowCloudCalendar: true,
    allowCloudDesktopAndDocuments: true,
    allowCloudDocumentSync: true,
    allowCloudKeychainSync: true,
    allowCloudMail: true,
    allowCloudNotes: true,
    allowCloudPhotoLibrary: true,
    allowCloudPrivateRelay: true,
    allowCloudReminders: true,
    allowContentCaching: true,
    allowContinuousPathKeyboard: true,
    allowDefinitionLookup: true,
    allowDeviceNameModification: true,
    allowDeviceSleep: true,
    allowDiagnosticSubmission: true,
    allowDiagnosticSubmissionModification: true,
    allowDictation: true,
    allowEnablingRestrictions: true,
    allowEnterpriseAppTrust: true,
    allowEnterpriseBookBackup: true,
    allowEnterpriseBookMetadataSync: true,
    allowEraseContentAndSettings: true,
    allowESIMModification: true,
    allowExplicitContent: true,
    allowFilesNetworkDriveAccess: true,
    allowFilesUSBDriveAccess: true,
    allowFindMyDevice: true,
    allowFindMyFriends: true,
    allowFindMyFriendsModification: true,
    allowFingerprintForUnlock: true,
    allowFingerprintModification: true,
    allowGameCenter: true,
    allowGlobalBackgroundFetchWhenRoaming: true,
    allowHostPairing: true,
    allowInAppPurchases: true,
    allowiTunes: true,
    allowiTunesFileSharing: true,
    allowKeyboardShortcuts: true,
    allowListedAppBundleIDs: null,
    allowLockScreenControlCenter: true,
    allowLockScreenNotificationsView: true,
    allowLockScreenTodayView: true,
    allowManagedAppsCloudSync: true,
    allowManagedToWriteUnmanagedContacts: false,
    allowMultiplayerGaming: true,
    allowMusicService: true,
    allowNews: true,
    allowNFC: true,
    allowNotificationsModification: true,
    allowOpenFromManagedToUnmanaged: true,
    allowOpenFromUnmanagedToManaged: true,
    allowOTAPKIUpdates: true,
    allowPairedWatch: true,
    allowPassbookWhileLocked: true,
    allowPasscodeModification: true,
    allowPasswordAutoFill: true,
    allowPasswordProximityRequests: true,
    allowPasswordSharing: true,
    allowPersonalHotspotModification: true,
    allowPhotoStream: true,
    allowPodcasts: true,
    allowPredictiveKeyboard: true,
    allowProximitySetupToNewDevice: true,
    allowRadioService: true,
    allowRemoteAppPairing: true,
    allowRemoteScreenObservation: true,
    allowSafari: true,
    allowScreenShot: true,
    allowSharedDeviceTemporarySession: true,
    allowSharedStream: true,
    allowSpellCheck: true,
    allowSpotlightInternetResults: true,
    allowSystemAppRemoval: true,
    allowUIAppInstallation: true,
    allowUIConfigurationProfileInstallation: true,
    allowUnmanagedToReadManagedContacts: false,
    allowUnpairedExternalBootToRecovery: false,
    allowUntrustedTLSPrompt: true,
    allowUSBRestrictedMode: true,
    allowVideoConferencing: true,
    allowVoiceDialing: true,
    allowVPNCreation: true,
    allowWallpaperModification: true,
    autonomousSingleAppModePermittedAppIDs: null,
    blockedAppBundleIDs: null,
    enforcedFingerprintTimeout: 172800,
    enforcedSoftwareUpdateDelay: 30,
    enforcedSoftwareUpdateMajorOSDeferredInstallDelay: 30,
    enforcedSoftwareUpdateMinorOSDeferredInstallDelay: 30,
    enforcedSoftwareUpdateNonOSDeferredInstallDelay: 30,
    forceAirDropUnmanaged: false,
    forceAirPlayIncomingRequestsPairingPassword: false,
    forceAirPlayOutgoingRequestsPairingPassword: false,
    forceAirPrintTrustedTLSRequirement: false,
    forceAssistantProfanityFilter: false,
    forceAuthenticationBeforeAutoFill: false,
    forceAutomaticDateAndTime: false,
    forceClassroomAutomaticallyJoinClasses: false,
    forceClassroomRequestPermissionToLeaveClasses: false,
    forceClassroomUnpromptedAppAndDeviceLock: false,
    forceClassroomUnpromptedScreenObservation: false,
    forceDelayedAppSoftwareUpdates: false,
    forceDelayedMajorSoftwareUpdates: false,
    forceDelayedSoftwareUpdates: false,
    forceEncryptedBackup: false,
    forceITunesStorePasswordEntry: false,
    forceLimitAdTracking: false,
    forceOnDeviceOnlyDictation: false,
    forceOnDeviceOnlyTranslation: false,
    forceWatchWristDetection: false,
    forceWiFiToAllowedNetworksOnly: false,
    forceWiFiPowerOn: false,
    forceWiFiWhitelisting: false,
    ratingApps: 1000,
    ratingMovies: 1000,
    ratingRegion: null,
    ratingTVShows: 1000,
    requireManagedPasteboard: false,
    safariAcceptCookies: 2,
    safariAllowAutoFill: true,
    safariAllowJavaScript: true,
    safariAllowPopups: true,
    safariForceFraudWarning: false,
    allowMailPrivacyProtection: true,
    payloadType: 'com.apple.applicationaccess',
    payloadVersion: 1,
    payloadIdentifier: 'com.rcksft.apple.applicationaccess',
  };

  public readonly udid = input.required<string>();
  private readonly _commandProgress = new Subject<CommandProgress>();
  public readonly commandProgress$: Observable<CommandProgress> = toObservable(this.udid).pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'InstallProfile'))
    )
  );
  public configurationProfile = new ConfigurationProfile();
  public showMore = false;

  constructor() {
    this.configurationProfile.payloadIdentifier = 'com.methylium.restrictions';
    this.configurationProfile.payloadOrganization = 'Methylium B.V.';
    this.configurationProfile.payloadDescription = 'This profile has pre-configured settings for restrictions.';
    this.configurationProfile.payloadDisplayName = 'Restrictions';

    this.payloadContent.payloadDescription =
      'Restrictions profile configures the device to use the specified Restrictions settings.';
    this.payloadContent.payloadDisplayName = 'Restrictions Profile';
    this.payloadContent.payloadIdentifier = 'com.methylium.restrictions';
    this.payloadContent.payloadOrganization = 'Methylium B.V.';
    this.payloadContent.payloadVersion = 1;
  }

  public installProfile(_udid: string) {
    // Try to stop editing on both components
    if (!this.restrictionsProfileComponent().doneEditing() || !this.configurationProfileComponent().doneEditing()) {
      return;
    }

    const command: CommandProgress = {
      udid: _udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'InstallProfile',
    };
    this._commandProgress.next(command);

    this.configurationProfile.payloadContent = [this.payloadContent as PayloadContent];
    this.mdmService.installProfile(_udid, this.configurationProfile).subscribe();
  }
}
